import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { type Category } from "@/lib/schema";
import {
  getMarketplaceListings,
  getMarketplaceCategories,
  getMarketplaceCategoriesHierarchy,
  type MarketplaceListing,
  type ListingFilters,
  type PaginatedResponse,
  type CategoryWithChildren,
  isValidImageUrl,
  getAlternativeImageUrl
} from "@/lib/marketplaceAPI";
import ProductCard from "@/components/ProductCard";
import {
  Package2,
  Wrench,
  Truck,
  Factory,
  Settings,
  type LucideIcon,
  Search,
  Tag,
  XCircle,
  CheckCircle2,
  Zap,
  Settings2,
  ArrowRight,
  X,
  Phone,
  Mail,
  ListChecks,
  Sparkles,
  FileText,
  SlidersHorizontal,
  Euro,
  Gauge,
  Building2,
  ArrowUpRight,
  AlertTriangle,
  Filter,
  MapPin,
  CalendarDays,
  ChevronLeft,
  Link,
  Check,
  Calendar,
  ZoomIn,
  MessageSquareIcon,
  InfoIcon,
  Loader2,
  ChevronRight,
  Camera,
  ListPlus,
  Grid3X3,
  List,
  FilterX,
  ArrowDown,
  ArrowUp,
  Minus,
  Plus,
  Minimize2,
  Maximize2,
} from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import useMediaQuery from "../hooks/useMediaQuery";

// Componente PriceRangeSlider
const PriceRangeSlider = ({
  value,
  onChange,
  min = 0,
  max = 50000,
  step = 1000
}: {
  value: [number, number];
  onChange: (value: [number, number]) => void;
  min?: number;
  max?: number;
  step?: number;
}) => {
  const [localValue, setLocalValue] = useState(value);

  // Actualiza el valor local cuando cambia el valor externo
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Handler para cambio de valores
  const handleChange = (newValue: [number, number]) => {
    // Asegurarse de que el valor mínimo nunca sea mayor que el máximo
    const sortedValue: [number, number] = [
      Math.min(newValue[0], newValue[1]),
      Math.max(newValue[0], newValue[1])
    ];
    setLocalValue(sortedValue);
  };

  // Handler para final de interacción
  const handleChangeEnd = () => {
    onChange(localValue);
  };

  // Handler para cambio del control izquierdo
  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Number(e.target.value);
    if (newMin <= localValue[1]) {
      handleChange([newMin, localValue[1]]);
    } else {
      // Si se intenta hacer mayor que el máximo, se establece el mismo valor para ambos
      handleChange([newMin, newMin]);
    }
  };

  // Handler para cambio del control derecho
  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Number(e.target.value);
    if (newMax >= localValue[0]) {
      handleChange([localValue[0], newMax]);
    } else {
      // Si se intenta hacer menor que el mínimo, se establece el mismo valor para ambos
      handleChange([newMax, newMax]);
    }
  };

  // Formatear precio para mostrar
  const formatPrice = (value: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="px-2">
      {/* Valores actuales */}
      <div className="flex justify-between mb-3 text-sm text-zinc-200">
        <span>{formatPrice(localValue[0])}</span>
        <span>{formatPrice(localValue[1])}</span>
      </div>

      {/* Slider */}
      <div className="h-2 bg-zinc-800 rounded-full relative">
        {/* Barra de progreso */}
        <div
          className="absolute h-full bg-[#FCAA0D] rounded-full"
          style={{
            left: `${((localValue[0] - min) / (max - min)) * 100}%`,
            right: `${100 - ((localValue[1] - min) / (max - min)) * 100}%`
          }}
        />

        {/* Control izquierdo */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue[0]}
          onChange={handleMinChange}
          onMouseUp={handleChangeEnd}
          onTouchEnd={handleChangeEnd}
          onBlur={handleChangeEnd}
          className="absolute w-full h-full appearance-none bg-transparent pointer-events-auto cursor-pointer"
          style={{
            zIndex: 2,
            WebkitAppearance: 'none',
            opacity: 0 // Hacemos el input invisible pero funcional
          }}
        />

        {/* Control derecho */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue[1]}
          onChange={handleMaxChange}
          onMouseUp={handleChangeEnd}
          onTouchEnd={handleChangeEnd}
          onBlur={handleChangeEnd}
          className="absolute w-full h-full appearance-none bg-transparent pointer-events-auto cursor-pointer"
          style={{
            zIndex: 3,
            WebkitAppearance: 'none',
            opacity: 0 // Hacemos el input invisible pero funcional
          }}
        />

        {/* Círculos de control */}
        <div
          className="absolute w-5 h-5 bg-white rounded-full cursor-pointer shadow-md hover:bg-[#FCAA0D] hover:scale-110 transition-all"
          style={{
            left: `${((localValue[0] - min) / (max - min)) * 100}%`,
            top: '50%',
            zIndex: 4,
            transform: 'translate(-50%, -50%)'
          }}
        />
        <div
          className="absolute w-5 h-5 bg-white rounded-full cursor-pointer shadow-md hover:bg-[#FCAA0D] hover:scale-110 transition-all"
          style={{
            left: `${((localValue[1] - min) / (max - min)) * 100}%`,
            top: '50%',
            zIndex: 4,
            transform: 'translate(-50%, -50%)'
          }}
        />
      </div>
    </div>
  );
};

// Componente Checkbox
const Checkbox = ({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) => {
  return (
    <label className="flex items-center gap-3 text-zinc-300 hover:text-white cursor-pointer group transition-colors">
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="sr-only" // Oculto visualmente pero accesible
        />
        <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${checked ? 'bg-[#FCAA0D]' : 'bg-zinc-800 group-hover:bg-zinc-700'}`}>
          {checked && <CheckCircle2 className="w-4 h-4 text-black" />}
        </div>
      </div>
      <span className="text-sm">{label}</span>
    </label>
  );
};

// Componente FilterChip para mostrar opciones seleccionables
const FilterChip = ({
  label,
  selected,
  onClick,
  count,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  count?: number;
}) => {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
        selected
          ? "bg-[#FCAA0D] text-black hover:bg-[#E89D00]"
          : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white"
      }`}
      aria-pressed={selected}
    >
      {label}
      {count !== undefined && (
        <span className={`text-xs px-1.5 py-0.5 rounded-full ${selected ? "bg-black/20" : "bg-black/30"}`}>
          {count}
        </span>
      )}
      {selected && <X className="w-3 h-3 hover:scale-110 transition-transform" />}
    </button>
  );
};

// Componente FilterAccordion para agrupar filtros
const FilterAccordion = ({
  title,
  icon,
  children,
  defaultOpen = false,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-zinc-800">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between text-white font-medium hover:bg-zinc-900/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span>{title}</span>
        </div>
        <div className={`transform transition-transform ${isOpen ? "rotate-180" : ""}`}>
          <ArrowRight className="w-4 h-4 rotate-90" />
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Componente ProductImage mejorado
const ProductImage = ({
  url,
  alt,
  className = "w-full h-full object-cover",
  onLoad,
  style
}: {
  url: string;
  alt: string;
  className?: string;
  onLoad?: () => void;
  style?: React.CSSProperties;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [imageSrc, setImageSrc] = useState(url);
  const [hasError, setHasError] = useState(false);

  // Manejar error de carga de imagen
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (hasError) {
      // Si ya intentamos con una URL alternativa y falló, usar imagen por defecto
      setImageSrc('/placeholder-image.png');
      return;
    }

    // Intentar con URL alternativa
    const alternativeUrl = getAlternativeImageUrl(url);
    if (alternativeUrl) {
      setImageSrc(alternativeUrl);
      setHasError(true);
    } else {
      // Si no hay URL alternativa, usar imagen por defecto
      setImageSrc('/placeholder-image.png');
    }
  };

  useEffect(() => {
    setImageSrc(url);
    setIsLoading(true);
    setHasError(false);
  }, [url]);

  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-800">
          <div className="w-8 h-8 border-2 border-zinc-600 border-t-[#FCAA0D] rounded-full animate-spin"></div>
        </div>
      )}
      <img
        src={imageSrc}
        alt={alt}
        className={className}
        onLoad={() => {
          setIsLoading(false);
          onLoad && onLoad();
        }}
        onError={handleImageError}
        style={style}
      />
    </div>
  );
};

// Modal de detalle de producto mejorado
const ProductDetailModal = ({
  product,
  isOpen,
  onClose
}: {
  product: MarketplaceListing | null;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { toast } = useToast();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [loadingImage, setLoadingImage] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [zoomLevel, setZoomLevel] = useState(2);

  if (!product) return null;

  // Formatear el precio para mostrar con el símbolo de euro
  const formattedPrice = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(product.price);

  // Fecha formateada
  const formattedDate = new Date(product.created_at).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const images = product.images && product.images.length > 0
    ? product.images
    : ['/placeholder-image.png'];

  const navigateImages = (direction: 'prev' | 'next') => {
    setLoadingImage(true);
    if (direction === 'prev') {
      setActiveImageIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
    } else {
      setActiveImageIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));
    }
    // Desactivar zoom al cambiar de imagen
    setIsZoomed(false);
  };

  // Función para copiar el enlace del producto
  const copyProductLink = () => {
    const baseUrl = window.location.origin;
    const productSlug = product.title
      .toLowerCase()
      .replace(/[^\w\sáéíóúüñ]/gi, '')
      .replace(/\s+/g, '-')
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    const url = `${baseUrl}/used-machinery?product=${product.id}-${productSlug}`;

    navigator.clipboard.writeText(url)
      .then(() => {
        setIsLinkCopied(true);

        toast({
          title: "Enlace copiado",
          description: "El enlace ha sido copiado al portapapeles",
          variant: "default",
        });

        setTimeout(() => {
          setIsLinkCopied(false);
        }, 2000);
      })
      .catch(err => {
        console.error('Error al copiar el enlace:', err);
        toast({
          title: "Error al copiar",
          description: "No se pudo copiar el enlace al portapapeles.",
          variant: "destructive",
        });
      });
  };

  // Función para compartir en WhatsApp
  const shareOnWhatsApp = () => {
    const baseUrl = window.location.origin;
    const productSlug = product.title
      .toLowerCase()
      .replace(/[^\w\sáéíóúüñ]/gi, '')
      .replace(/\s+/g, '-')
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    const url = `${baseUrl}/used-machinery?product=${product.id}-${productSlug}`;
    const message = `¡Mira esta máquina de ocasión en Innotec! ${product.title}: ${url}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

    window.open(whatsappUrl, '_blank');
  };

  // Función para manejar el zoom de la imagen
  const handleImageZoom = () => {
    if (images.length > 0) {
      setIsZoomed(!isZoomed);
      if (isFullscreen && !isZoomed) {
        // Si estamos en pantalla completa y estamos activando el zoom, aumentamos el nivel
        setZoomLevel(3);
      } else {
        // De lo contrario, volvemos al nivel normal
        setZoomLevel(2);
      }
    }
  };

  // Función para manejar el movimiento del ratón cuando está activado el zoom
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current || !isZoomed) return;

    const { left, top, width, height } = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;

    setMousePosition({ x, y });
  };

  // Función para manejar la carga de imagen
  const handleImageLoad = () => {
    setLoadingImage(false);
  };

  // Función para alternar el modo de pantalla completa de la imagen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (isZoomed) {
      // Si estamos con zoom, ajustamos el nivel según estemos entrando o saliendo
      setZoomLevel(isFullscreen ? 2 : 3);
    }
  };

  // Función para ajustar el nivel de zoom
  const adjustZoom = (increment: boolean) => {
    if (isZoomed) {
      setZoomLevel(prev => {
        const newLevel = increment ? prev + 0.5 : prev - 0.5;
        return Math.max(1.5, Math.min(5, newLevel)); // Limitamos entre 1.5x y 5x
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${isFullscreen ? 'max-w-none w-screen h-screen max-h-screen' : 'max-w-6xl w-[95vw] max-h-[90vh]'} bg-gradient-to-b from-[#0c0d0e] to-[#111213] text-white border-white/10 overflow-auto z-[9999] fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] shadow-2xl shadow-black/50 rounded-xl transition-all duration-300`}>
        <DialogTitle className="sr-only">{product.title}</DialogTitle>
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 bg-black/30 hover:bg-black/50 transition-all text-white z-50"
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" />
        </button>

        <div className={`grid grid-cols-1 ${isFullscreen ? '' : 'lg:grid-cols-2'} gap-0 h-full`}>
          {/* Columna izquierda: Galería de imágenes */}
          <div className={`flex flex-col ${isFullscreen ? 'h-full' : ''}`}>
            <div className="relative h-full flex flex-col">
              <div
                ref={imageRef}
                onClick={handleImageZoom}
                onMouseMove={handleMouseMove}
                className={`${isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'} ${isFullscreen ? 'h-[calc(100vh-5rem)]' : 'aspect-square'} bg-zinc-800/70 backdrop-blur-sm rounded-lg overflow-hidden relative transition-all duration-200 flex-grow`}
              >
                {loadingImage && (
                  <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/70 z-10">
                    <Loader2 className="w-8 h-8 text-[#FCAA0D] animate-spin" />
                  </div>
                )}

                <ProductImage
                  url={images[activeImageIndex]}
                  alt={product.title}
                  className={`w-full h-full object-contain transition-opacity duration-300 ${loadingImage ? 'opacity-0' : 'opacity-100'}`}
                  onLoad={handleImageLoad}
                  style={isZoomed ? {
                    transformOrigin: `${mousePosition.x}% ${mousePosition.y}%`,
                    transform: `scale(${zoomLevel})`,
                    cursor: 'zoom-out'
                  } : {}}
                />

                {/* Overlay para indicar que es clickeable */}
                {!isZoomed && !loadingImage && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <div className="rounded-full bg-black/40 p-3">
                      <ZoomIn className="h-6 w-6 text-white" />
                    </div>
                  </div>
                )}

                {/* Control de zoom */}
                {isZoomed && (
                  <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-black/60 rounded-full p-1.5 backdrop-blur-sm z-20">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        adjustZoom(false);
                      }}
                      className="p-1 rounded-full hover:bg-black/40 transition-colors"
                      aria-label="Reducir zoom"
                    >
                      <Minus className="h-4 w-4 text-white" />
                    </button>
                    <span className="text-xs text-white font-medium">{zoomLevel.toFixed(1)}x</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        adjustZoom(true);
                      }}
                      className="p-1 rounded-full hover:bg-black/40 transition-colors"
                      aria-label="Aumentar zoom"
                    >
                      <Plus className="h-4 w-4 text-white" />
                    </button>
                  </div>
                )}

                {/* Controles de navegación mejorados */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateImages('prev');
                      }}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 p-3 rounded-full text-white transition-all z-10 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] focus:ring-opacity-50"
                      aria-label="Imagen anterior"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateImages('next');
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 p-3 rounded-full text-white transition-all z-10 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] focus:ring-opacity-50"
                      aria-label="Imagen siguiente"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}

                {/* Botón de pantalla completa */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFullscreen();
                  }}
                  className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 p-2 rounded-full text-white transition-all z-20 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]"
                  aria-label={isFullscreen ? "Salir de pantalla completa" : "Ver en pantalla completa"}
                >
                  {isFullscreen ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </button>

                {/* Indicador de posición mejorado */}
                {images.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-xs font-semibold z-10 flex items-center gap-1">
                    <Camera className="w-3 h-3 text-[#FCAA0D]" />
                    {activeImageIndex + 1} / {images.length}
                  </div>
                )}
              </div>

              {/* Miniaturas mejoradas - No mostrar en modo pantalla completa */}
              {!isFullscreen && images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto py-3 mt-3 px-1 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-zinc-900/20">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveImageIndex(index);
                        setLoadingImage(true);
                      }}
                      className={`w-20 h-20 rounded-md overflow-hidden flex-shrink-0 border-2 transition-all hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] ${
                        activeImageIndex === index ? 'border-[#FCAA0D] shadow-md shadow-[#FCAA0D]/20' : 'border-transparent'
                      }`}
                      aria-label={`Ver imagen ${index + 1}`}
                    >
                      <ProductImage url={img} alt={`Miniatura ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Características y especificaciones - Ocultar en modo pantalla completa */}
            {!isFullscreen && (
              <div className="px-4 flex-1 overflow-hidden mt-4">
                <div className="bg-gradient-to-br from-zinc-800/60 to-zinc-800/30 backdrop-blur-sm rounded-xl p-5 h-full flex flex-col border border-white/5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-[#FCAA0D]/10">
                      <ListChecks className="w-5 h-5 text-[#FCAA0D]" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      Características
                    </h3>
                  </div>

                  <div className="space-y-3 overflow-y-auto pr-2 flex-1 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
                    {product.features && product.features.length > 0 ? (
                      product.features.map((feature, index) => (
                        <div key={`feature-${index}`} className="flex items-center gap-2 text-zinc-300 hover:text-white transition-colors">
                          <CheckCircle2 className="w-4 h-4 text-[#FCAA0D] shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-zinc-500 text-sm italic flex items-center gap-2">
                        <InfoIcon className="w-4 h-4" />
                        No hay características disponibles
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Columna derecha: Información del producto - Ocultar en modo pantalla completa */}
          {!isFullscreen && (
            <div className="p-4 md:p-6 lg:p-8 flex flex-col h-full border-l border-zinc-800/50">
              {/* Encabezado con información clave */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="px-3 py-1.5 rounded-full bg-[#FCAA0D]/10 text-[#FCAA0D] text-sm font-medium">
                    {product.brand}
                  </span>
                  {product.year && (
                    <span className="px-3 py-1.5 rounded-full bg-zinc-800/80 backdrop-blur-sm text-white text-xs font-medium flex items-center gap-1.5">
                      <CalendarDays className="w-3 h-3 text-[#FCAA0D]" />
                      {product.year}
                    </span>
                  )}
                </div>

                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 tracking-tight">
                  {product.title}
                </h2>

                <div className="flex items-baseline gap-2 mb-5">
                  {/* <span className="text-3xl text-[#FCAA0D] font-bold">
                    {formattedPrice}
                  </span>
                  <span className="text-zinc-400 text-sm">IVA no incluido</span> */}
                </div>

                <div className="bg-zinc-800/20 border border-white/5 rounded-xl p-4 mb-6">
                  <p className="text-zinc-300 text-base leading-relaxed">
                    {product.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                  <div className="bg-gradient-to-r from-zinc-800/30 to-zinc-800/10 backdrop-blur-sm rounded-xl p-4 border border-white/5 hover:border-white/10 transition-colors group">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4 text-[#FCAA0D] group-hover:scale-110 transition-transform" />
                      <span className="text-zinc-400 text-sm">Marca</span>
                    </div>
                    <span className="text-white font-medium">{product.brand}</span>
                  </div>

                  {product.location && (
                    <div className="bg-gradient-to-r from-zinc-800/30 to-zinc-800/10 backdrop-blur-sm rounded-xl p-4 border border-white/5 hover:border-white/10 transition-colors group">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-[#FCAA0D] group-hover:scale-110 transition-transform" />
                        <span className="text-zinc-400 text-sm">Ubicación</span>
                      </div>
                      <span className="text-white font-medium">{product.location}</span>
                    </div>
                  )}

                  {product.year && (
                    <div className="bg-gradient-to-r from-zinc-800/30 to-zinc-800/10 backdrop-blur-sm rounded-xl p-4 border border-white/5 hover:border-white/10 transition-colors group">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-[#FCAA0D] group-hover:scale-110 transition-transform" />
                        <span className="text-zinc-400 text-sm">Año</span>
                      </div>
                      <span className="text-white font-medium">{product.year}</span>
                    </div>
                  )}

                  <div className="bg-gradient-to-r from-zinc-800/30 to-zinc-800/10 backdrop-blur-sm rounded-xl p-4 border border-white/5 hover:border-white/10 transition-colors group">
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarDays className="w-4 h-4 text-[#FCAA0D] group-hover:scale-110 transition-transform" />
                      <span className="text-zinc-400 text-sm">Publicado</span>
                    </div>
                    <span className="text-white font-medium">{formattedDate}</span>
                  </div>
                </div>
              </div>

              {/* NUEVA SECCIÓN: Especificaciones Técnicas */}
              {product.technical_specs && product.technical_specs.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-[#FCAA0D]/10">
                      <Settings2 className="w-5 h-5 text-[#FCAA0D]" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      Especificaciones Técnicas
                    </h3>
                  </div>
                  <div className="bg-gradient-to-r from-zinc-800/30 to-zinc-800/10 backdrop-blur-sm rounded-xl p-5 border border-white/5 space-y-3">
                    {product.technical_specs.map((spec, index) => (
                      <div key={`spec-${index}`} className="grid grid-cols-3 gap-2 text-sm">
                        <span className="col-span-1 font-medium text-zinc-400">{spec.label}:</span>
                        <span className="col-span-2 text-white">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Botones de acción */}
              <div className="mt-auto space-y-5">
                {/* Título de sección */}
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquareIcon className="w-4 h-4 text-[#FCAA0D]" />
                  <h3 className="text-sm font-medium text-zinc-400">Contactar con el vendedor</h3>
                </div>

                {/* Botones principales */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-medium py-3.5 rounded-xl flex items-center justify-center gap-2.5 shadow-lg shadow-[#FCAA0D]/10 transition-all duration-300 hover:scale-[1.02]">
                    <Phone className="w-5 h-5" />
                    Contactar por teléfono
                  </Button>
                  <Button className="bg-zinc-800 hover:bg-zinc-700 text-white font-medium py-3.5 rounded-xl flex items-center justify-center gap-2.5 border border-white/5 hover:border-white/10 transition-all duration-300 hover:scale-[1.02]">
                    <Mail className="w-5 h-5 text-[#FCAA0D]" />
                    Enviar mensaje
                  </Button>
                </div>

                {/* Separador */}
                <div className="flex items-center gap-4 my-6">
                  <div className="h-px bg-zinc-800 flex-grow"></div>
                  <span className="text-xs text-zinc-500">Compartir</span>
                  <div className="h-px bg-zinc-800 flex-grow"></div>
                </div>

                {/* Botones de compartir - Simplificado, solo WhatsApp y copiar enlace */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      shareOnWhatsApp();
                    }}
                    className="flex-1 bg-[#25D366] text-zinc-900 font-bold px-4 py-3 rounded-xl hover:bg-[#22c35e] transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-[#25D366]/10 hover:scale-[1.02]"
                    aria-label="Compartir por WhatsApp"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.768.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564c.173.087.289.13.332.202.043.72.043.433-.101.824z"/>
                    </svg>
                    <span className="whitespace-nowrap">WhatsApp</span>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyProductLink();
                    }}
                    className="flex-1 bg-zinc-800 text-white font-bold px-4 py-3 rounded-xl hover:bg-zinc-700 transition-all duration-300 flex items-center justify-center gap-2 relative border border-white/5 hover:border-white/10 hover:scale-[1.02]"
                    aria-label="Copiar enlace"
                  >
                    {isLinkCopied ? (
                      <>
                        <Check className="w-5 h-5 text-[#FCAA0D]" />
                        <span className="whitespace-nowrap">Enlace copiado</span>
                      </>
                    ) : (
                      <>
                        <Link className="w-5 h-5 text-[#FCAA0D]" />
                        <span className="whitespace-nowrap">Copiar enlace</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function UsedMachinery() {
  // Estados para filtros
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 50000]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedParentCategory, setSelectedParentCategory] = useState<number | null>(null);
  const [selectedChildCategory, setSelectedChildCategory] = useState<number | null>(null);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [sortOrder, setSortOrder] = useState<"newest" | "price_asc" | "price_desc">("newest");
  const [selectedProduct, setSelectedProduct] = useState<MarketplaceListing | null>(null);
  const { toast } = useToast();

  // Detectar si estamos en móvil
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Por defecto, el panel de filtros está cerrado en móvil y abierto en desktop
  useEffect(() => {
    setIsFilterPanelOpen(!isMobile);
  }, [isMobile]);

  // Estado para controlar si estamos en medio de una interacción
  const [isInteracting, setIsInteracting] = useState(false);

  // Reset de página al cambiar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [
    priceRange,
    selectedBrands,
    selectedParentCategory, // Nuevo estado
    selectedChildCategory,  // Nuevo estado
    selectedLocations,
    searchQuery,
    sortOrder
  ]);

  // Construir objeto de filtros para la API
  const buildFilters = (): ListingFilters => {
    const filters: ListingFilters = {
      page: currentPage,
      limit: itemsPerPage,
      sort: sortOrder
    };

    if (searchQuery) {
      filters.query = searchQuery;
    }

    if (priceRange[0] > 0) {
      filters.price_min = priceRange[0];
    }

    if (priceRange[1] < 50000) {
      filters.price_max = priceRange[1];
    }

    if (selectedBrands.length > 0) {
      filters.brand = selectedBrands;
    }

    // Lógica de category_id basada en selectedParentCategory y selectedChildCategory
    let categoryIdsToFilter: number[] = [];
    if (selectedChildCategory !== null) {
      categoryIdsToFilter = [selectedChildCategory];
    } else if (selectedParentCategory !== null) {
      const parentCat = categoriesHierarchy?.find(cat => cat.id === selectedParentCategory);
      if (parentCat) {
        categoryIdsToFilter = [parentCat.id, ...(parentCat.children?.map(child => child.id) || [])];
      }
    }
    if (categoryIdsToFilter.length > 0) {
      filters.category_id = categoryIdsToFilter.filter(id => id !== null && id !== undefined) as number[];
    }

    if (selectedLocations.length > 0) {
      filters.location = selectedLocations;
    }
    console.log("[UsedMachinery] Building filters:", filters); // LOG AÑADIDO
    return filters;
  };

  // Fetch categories
  const { data: categoriesHierarchy, isLoading: categoriesLoading } = useQuery<CategoryWithChildren[]>({ // USAR CategoryWithChildren[]
    queryKey: ["marketplace-categories-hierarchy"],
    queryFn: getMarketplaceCategoriesHierarchy,
    staleTime: Infinity,
  });

  // Fetch products with filters
  const {
    data: listingsResponse,
    isLoading: listingsLoading,
    error: listingsError,
    refetch: refetchListings
  } = useQuery<PaginatedResponse<MarketplaceListing>>({
    queryKey: ["marketplace-listings", buildFilters()],
    queryFn: () => {
      const currentFilters = buildFilters();
      console.log("[UsedMachinery] Calling getMarketplaceListings with filters:", currentFilters); // LOG AÑADIDO
      return getMarketplaceListings(currentFilters);
    },
    staleTime: 30000, // 30 segundos
  });

  // Estado para controlar si es la primera carga
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Marcar la primera carga como completada una vez
  useEffect(() => {
    if (isFirstLoad && !categoriesLoading && !listingsLoading) {
      setIsFirstLoad(false);
    }
  }, [categoriesLoading, listingsLoading, isFirstLoad]);

  // Extraer los productos y la información de paginación
  const products = listingsResponse?.data || [];
  const pagination = listingsResponse?.pagination || {
    page: 1,
    limit: itemsPerPage,
    total: 0,
    pages: 0
  };

  // Obtener una lista de marcas, ubicaciones y estados de los datos
  const availableBrands = useMemo(() => {
    // Primero intentamos extraer las marcas de los datos, si no, usamos las de ejemplo
    if (products.length > 0) {
      return [...new Set(products.map((product: MarketplaceListing) => product.brand))];
    }
    return ["WAAGE", "Treif", "Vemag", "Laska", "Handtmann", "Inject Star"];
  }, [products]);

  const locations = useMemo(() => {
    // Extraer ubicaciones de los datos, si no hay, usamos las de ejemplo
    if (products.length > 0) {
      return [...new Set(products
        .filter((p: MarketplaceListing) => p.location)
        .map((p: MarketplaceListing) => p.location as string)
      )];
    }
    return ["Barcelona", "Madrid", "Valencia", "Sevilla", "Bilbao", "Zaragoza"];
  }, [products]);

  // Toggle brand selection
  const toggleBrand = (brand: string) => {
    setSelectedBrands(prev => {
      return prev.includes(brand)
        ? prev.filter(b => b !== brand)
        : [...prev, brand];
    });
  };

  const handleParentCategorySelect = (categoryId: number | null) => {
    if (selectedParentCategory === categoryId || categoryId === null) {
      setSelectedParentCategory(null);
      setSelectedChildCategory(null);
    } else {
      setSelectedParentCategory(categoryId);
      setSelectedChildCategory(null); // Reset child when parent changes
    }
  };

  const handleChildCategorySelect = (subcategoryId: number | null) => {
    // No permitir seleccionar un hijo si no hay padre activo (aunque la UI debería prevenirlo)
    if (selectedParentCategory === null && subcategoryId !== null) return;

    if (selectedChildCategory === subcategoryId || subcategoryId === null) {
      setSelectedChildCategory(null);
    } else {
      setSelectedChildCategory(subcategoryId);
    }
  };

  // Nueva función para seleccionar/deseleccionar una categoría padre Y TODOS sus hijos
  // ESTA FUNCIÓN YA NO ES NECESARIA CON EL NUEVO ENFOQUE
  // const toggleParentCategoryAndChildren = (category: CategoryWithChildren) => { ... };

  // Cambiar página
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Resetear filtros
  const resetFilters = () => {
    setPriceRange([0, 50000]);
    setSelectedBrands([]);
    setSelectedParentCategory(null); // Resetear nuevo estado
    setSelectedChildCategory(null);  // Resetear nuevo estado
    setSelectedLocations([]);
    setSearchQuery(""); // Limpiar searchQuery
    setSortOrder("newest");
    setCurrentPage(1);

    // Forzar una actualización inmediata de los datos
    setTimeout(() => {
      refetchListings();
    }, 100);
  };

  // Contador de filtros activos
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (priceRange[0] > 0 || priceRange[1] < 50000) count++;
    count += selectedBrands.length;
    if (selectedParentCategory !== null) count++; // Contar si hay categoría padre/hijo seleccionada
    count += selectedLocations.length;
    if (searchQuery) count++;
    return count;
  }, [priceRange, selectedBrands, selectedParentCategory, selectedChildCategory, selectedLocations, searchQuery]);

  // Panel lateral de filtros actualizado
  const FilterPanel = () => (
    <div className="bg-zinc-900 p-6 rounded-lg shadow-xl space-y-6 h-full overflow-y-auto">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-semibold text-white flex items-center">
          <Filter className="mr-3 text-[#FCAA0D]" />
          Filtrar Maquinaria
        </h3>
        <Button
          variant="ghost"
          size="icon"
          onClick={closeFilterPanel}
          className="text-zinc-400 hover:text-white md:hidden"
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      {/* Filtro de Categorías Jerárquico */}
      <FilterAccordion
        title="Categorías"
        icon={<ListChecks className="text-[#FCAA0D] h-5 w-5" />}
        defaultOpen={true}
      >
        <div className="space-y-3">
          {(categoriesHierarchy || []).map((category: CategoryWithChildren) => (
            <div key={category.id}>
              <Button
                variant="ghost"
                className={`w-full justify-start text-left h-auto py-2 px-3 rounded-md transition-colors
                  ${selectedParentCategory === category.id ? 'bg-zinc-700 text-white' : 'text-zinc-300 hover:bg-zinc-800 hover:text-white'}`}
                onClick={() => handleParentCategorySelect(category.id)}
              >
                <div className="flex items-center justify-between w-full">
                  <span className="font-medium">{category.name}</span>
                  {selectedParentCategory === category.id ? (
                    <Minus className="h-4 w-4" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </div>
              </Button>
              <AnimatePresence>
                {selectedParentCategory === category.id && category.children && category.children.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="pl-4 mt-2 space-y-2"
                  >
                    {category.children.map((child: CategoryWithChildren) => (
                      <Button
                        key={child.id}
                        variant="ghost"
                        className={`w-full justify-start text-left h-auto py-2 px-3 rounded-md transition-colors text-sm
                          ${selectedChildCategory === child.id ? 'bg-zinc-600 text-white' : 'text-zinc-400 hover:bg-zinc-700 hover:text-white'}`}
                        onClick={() => handleChildCategorySelect(child.id)}
                      >
                        {child.name}
                      </Button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
          {(selectedParentCategory || selectedChildCategory) && (
             <Button
                variant="link"
                className="text-xs text-[#FCAA0D] hover:text-yellow-400 px-0"
                onClick={() => {
                  setSelectedParentCategory(null);
                  setSelectedChildCategory(null);
                }}
              >
                Limpiar selección de categoría
              </Button>
          )}
        </div>
      </FilterAccordion>

      {/* Filtro de Rango de Precios */}
      <FilterAccordion
        title="Rango de Precios"
        icon={<Euro className="text-[#FCAA0D] h-5 w-5" />}
        defaultOpen={true}
      >
        <PriceRangeSlider
          value={priceRange}
          onChange={setPriceRange}
          min={0}
          max={50000} // Ajusta según el rango máximo de tus productos
          step={100}  // Ajusta el paso del slider
        />
      </FilterAccordion>

      {/* Filtro de Ubicación */}
      <FilterAccordion
        title="Ubicación"
        icon={<MapPin className="text-[#FCAA0D] h-5 w-5" />}
      >
        <div className="space-y-2">
          {locations.map((location: string) => (
            <Checkbox
              key={location}
              label={location}
              checked={selectedLocations.includes(location)}
              onChange={() => toggleLocation(location)} // Asegurarse que toggleLocation está definida
            />
          ))}
        </div>
         {selectedLocations.length > 0 && (
            <Button
              variant="link"
              className="text-xs text-[#FCAA0D] hover:text-yellow-400 mt-2 px-0"
              onClick={() => setSelectedLocations([])}
            >
              Limpiar ubicaciones
            </Button>
          )}
      </FilterAccordion>

      {/* Filtro de Marcas */}
      <FilterAccordion
        title="Marcas"
        icon={<Tag className="text-[#FCAA0D] h-5 w-5" />}
      >
        <div className="space-y-2">
          {availableBrands.map((brand) => (
            <Checkbox
              key={brand}
              label={brand}
              checked={selectedBrands.includes(brand)}
              onChange={() => toggleBrand(brand)}
            />
          ))}
        </div>
        {selectedBrands.length > 0 && (
            <Button
              variant="link"
              className="text-xs text-[#FCAA0D] hover:text-yellow-400 mt-2 px-0"
              onClick={() => setSelectedBrands([])}
            >
              Limpiar marcas
            </Button>
          )}
      </FilterAccordion>


      {/* Botón para limpiar todos los filtros */}
      <Button
        onClick={resetFilters}
        variant="outline"
        className="w-full border-[#FCAA0D] text-[#FCAA0D] hover:bg-[#FCAA0D] hover:text-black transition-colors flex items-center gap-2"
      >
        <FilterX className="h-4 w-4" />
        Limpiar Todos los Filtros
      </Button>
    </div>
  );

  // Loading state - solo para la carga inicial, nunca para actualizaciones de filtro
  if (isFirstLoad && (categoriesLoading || listingsLoading)) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Error state
  if (listingsError) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="text-center text-red-500 p-4 bg-red-900/20 rounded-lg">
          <p className="text-lg font-semibold">Error al cargar los productos</p>
          <p className="text-sm mt-2">Por favor, inténtalo de nuevo más tarde</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-[#FCAA0D] text-black rounded-lg"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Renderizado de un producto
  const renderProduct = (product: MarketplaceListing) => {
    // Formatear el precio para mostrar con el símbolo de euro
    const formattedPrice = new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(product.price);

    // Fecha formateada
    const formattedDate = new Date(product.created_at).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return (
      <motion.div
        key={product.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.3 }}
        layout
        onClick={() => setSelectedProduct(product)}
        className="bg-zinc-900/50 backdrop-blur-sm rounded-xl overflow-hidden border border-zinc-800/50 hover:border-[#FCAA0D]/20 transition-all duration-300 group cursor-pointer"
      >
        {/* Imagen del producto */}
        <div className="relative h-48 overflow-hidden bg-zinc-800">
          {product.images && product.images[0] ? (
            <ProductImage
              url={product.images[0]}
              alt={product.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package2 className="w-12 h-12 text-zinc-700" />
            </div>
          )}
          {/* Badge de ubicación */}
          {product.location && (
            <div className="absolute bottom-4 left-4">
              <span className="px-3 py-1 bg-black/60 backdrop-blur-sm text-white rounded-full text-xs font-medium flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {product.location}
              </span>
            </div>
          )}

          {/* Badge de año */}
          {product.year && (
            <div className="absolute bottom-4 right-4">
              <span className="px-3 py-1 bg-black/60 backdrop-blur-sm text-white rounded-full text-xs font-medium flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                {product.year}
              </span>
            </div>
          )}
        </div>

        {/* Contenido del producto */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#FCAA0D] transition-colors">
                {product.title}
              </h3>
              <span className="text-sm text-zinc-400">{product.brand}</span>
            </div>
            <div className="text-right">
              {/* <span className="text-[#FCAA0D] font-semibold">{formattedPrice}</span> */}
            </div>
          </div>

          <p className="text-zinc-400 text-sm mb-4 line-clamp-2">
            {product.description}
          </p>

          {/* Especificaciones principales */}
          <div className="space-y-2 mb-6">
            {product.features?.slice(0, 3).map((feature, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-zinc-300">
                <CheckCircle2 className="w-4 h-4 text-[#FCAA0D]" />
                <span>{feature}</span>
              </div>
            ))}
          </div>

          {/* Botones de acción */}
          <div className="flex gap-2">
            <button className="flex-1 bg-[#FCAA0D] text-black px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#FCAA0D]/90 transition-colors flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" />
              Contactar
            </button>
            <button className="flex-1 bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2">
              <FileText className="w-4 h-4" />
              Detalles
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  // Manejador específico para cerrar el panel en móvil
  const closeFilterPanel = () => {
    if (isMobile) {
      setIsFilterPanelOpen(false);
    }
  };

  // Toggle location selection - Asegurar que esta función exista y sea correcta
  const toggleLocation = (location: string) => {
    setSelectedLocations(prev => {
      return prev.includes(location)
        ? prev.filter(l => l !== location)
        : [...prev, location];
    });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] relative">
      {/* Modal de detalle de producto - Ahora renderizado fuera del layout principal para estar por encima de todo */}
      <ProductDetailModal
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Panel de filtros (columna izquierda) */}
          <div className="md:col-span-1">
            <div className="bg-zinc-900 rounded-lg border border-zinc-800 sticky top-6">
              {/* Cabecera de filtros */}
              <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-[#FCAA0D]" />
                  <h2 className="text-lg font-bold text-white">Filtros</h2>
                </div>
                {activeFiltersCount > 0 && (
                  <button
                    onClick={resetFilters}
                    className="text-xs text-[#FCAA0D] hover:underline"
                  >
                    Limpiar todo
                  </button>
                )}
              </div>

              {/* Filtros activos */}
              {activeFiltersCount > 0 && (
                <div className="p-4 border-b border-zinc-800">
                  <div className="mb-2">
                    <span className="text-sm text-zinc-400">Filtros activos</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {priceRange[0] > 0 || priceRange[1] < 50000 ? (
                      <FilterChip
                        label={`${new Intl.NumberFormat('es-ES', {style: 'currency', currency: 'EUR', maximumFractionDigits: 0}).format(priceRange[0])} - ${new Intl.NumberFormat('es-ES', {style: 'currency', currency: 'EUR', maximumFractionDigits: 0}).format(priceRange[1])}`}
                        selected={true}
                        onClick={() => setPriceRange([0, 50000])}
                      />
                    ) : null}

                    {/* Chips para categorías seleccionadas */}
                    {selectedParentCategory !== null && categoriesHierarchy?.find(c => c.id === selectedParentCategory) && (
                      <FilterChip
                        key={`cat-parent-${selectedParentCategory}`}
                        label={categoriesHierarchy.find(c => c.id === selectedParentCategory)?.name || 'Categoría'}
                        selected={true}
                        onClick={() => {
                          handleParentCategorySelect(null); // Esto limpiará padre e hijo
                        }}
                      />
                    )}
                    {selectedChildCategory !== null && selectedParentCategory !== null &&
                      categoriesHierarchy?.find(c => c.id === selectedParentCategory)?.children?.find(sub => sub.id === selectedChildCategory) && (
                      <FilterChip
                        key={`cat-child-${selectedChildCategory}`}
                        label={categoriesHierarchy.find(c => c.id === selectedParentCategory)?.children?.find(sub => sub.id === selectedChildCategory)?.name || 'Subcategoría'}
                        selected={true}
                        onClick={() => {
                          handleChildCategorySelect(null); // Limpiar solo hijo
                        }}
                      />
                    )}

                    {selectedBrands.map(brand => (
                      <FilterChip
                        key={`brand-${brand}`}
                        label={brand}
                        selected={true}
                        onClick={() => toggleBrand(brand)}
                      />
                    ))}

                    {selectedLocations.map(location => (
                      <FilterChip
                        key={`location-${location}`}
                        label={location}
                        selected={true}
                        onClick={() => toggleLocation(location)}
                      />
                    ))}

                    {/* Mostrar searchQuery como un chip si está activo */}
                    {searchQuery && (
                      <FilterChip
                        label={`Búsqueda: "${searchQuery}"`}
                        selected={true}
                        onClick={() => setSearchQuery("")}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Filtro: Precio */}
              <div className="border-b border-zinc-800">
                <div className="p-4">
                  <h3 className="text-white font-medium flex items-center gap-2 mb-4">
                    <Euro className="w-4 h-4 text-[#FCAA0D]" />
                    Precio
                  </h3>
                  <PriceRangeSlider value={priceRange} onChange={setPriceRange} />
                </div>
              </div>

              {/* Filtro: Categorías - Este es el panel lateral principal que ya usa la lógica jerárquica */}
              {/* No necesita checkboxes aquí, la interacción es con los botones jerárquicos */}
              <div className="border-b border-zinc-800">
                  <div className="p-4">
                      <h3 className="text-white font-medium flex items-center gap-2 mb-1">
                          <Package2 className="w-4 h-4 text-[#FCAA0D]" />
                          Categorías
                      </h3>
                  </div>
                  <div className="px-2 pb-2 space-y-1">
                      {categoriesHierarchy?.map(category => (
                          <div key={category.id} className="border-b border-zinc-800 last:border-b-0">
                              <button
                                  className={`w-full flex items-center justify-between py-3 px-2 rounded-md hover:bg-zinc-800/60 transition-colors group ${
                                      selectedParentCategory === category.id ? 'bg-zinc-800' : ''
                                  }`}
                                  onClick={() => handleParentCategorySelect(category.id)}
                              >
                                  <span className={`text-sm font-medium ${
                                      selectedParentCategory === category.id ? 'text-[#FCAA0D]' : 'text-zinc-200 group-hover:text-white'
                                  }`}>
                                      {category.name}
                                  </span>
                                  {category.children && category.children.length > 0 && (
                                     <ArrowRight className={`w-4 h-4 text-zinc-500 transform transition-transform ${
                                         selectedParentCategory === category.id ? 'rotate-90 text-[#FCAA0D]' : 'group-hover:text-zinc-300'
                                        }`}
                                     />
                                  )}
                              </button>
                              {selectedParentCategory === category.id && category.children && category.children.length > 0 && (
                                  <div className="pl-4 pr-2 pt-2 pb-2 space-y-1 bg-zinc-800/30 rounded-b-md">
                                      {category.children.map((subcategory: CategoryWithChildren) => (
                                          <button
                                              key={subcategory.id}
                                              className={`w-full text-left py-2 px-3 rounded-md hover:bg-zinc-700/50 transition-colors text-xs ${
                                                  selectedChildCategory === subcategory.id ? 'bg-[#FCAA0D] text-black font-semibold' : 'text-zinc-300 hover:text-white'
                                              }`}
                                              onClick={(e) => {
                                                  e.stopPropagation(); // Prevenir que el click se propague al botón padre
                                                  handleChildCategorySelect(subcategory.id);
                                              }}
                                          >
                                              {subcategory.name}
                                          </button>
                                      ))}
                                  </div>
                              )}
                          </div>
                      ))}
                  </div>
              </div>

              {/* Filtro: Ubicación */}
              <div className="border-b border-zinc-800">
                <div className="p-4">
                  <h3 className="text-white font-medium flex items-center gap-2 mb-4">
                    <MapPin className="w-4 h-4 text-[#FCAA0D]" />
                    Ubicación
                  </h3>
                  <div className="space-y-3">
                    {locations.map(location => (
                      <Checkbox
                        key={location}
                        label={location}
                        checked={selectedLocations.includes(location)}
                        onChange={() => toggleLocation(location)} // Asegurarse que toggleLocation está definida
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Filtro: Marcas */}
              <div className="border-b border-zinc-800">
                <div className="p-4">
                  <h3 className="text-white font-medium flex items-center gap-2 mb-4">
                    <Building2 className="w-4 h-4 text-[#FCAA0D]" />
                    Marcas
                  </h3>
                  <div className="space-y-3">
                    {availableBrands.map(brand => (
                      <Checkbox
                        key={brand}
                        label={brand}
                        checked={selectedBrands.includes(brand)}
                        onChange={() => toggleBrand(brand)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contenido Principal (columna derecha) */}
          <div className="md:col-span-3">

            {/* NUEVA BARRA DE BÚSQUEDA CENTRALIZADA */}
            <div className="mb-6 bg-zinc-900 rounded-lg border border-zinc-800 p-4">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por nombre, marca, descripción..."
                  className="w-full bg-zinc-800 text-white placeholder-zinc-500 rounded-lg pl-10 pr-10 py-3 outline-none focus:ring-1 focus:ring-[#FCAA0D]/50 transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-white p-1"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                )}
              </div>
              {/* Aquí se podrían añadir los "Filtros Rápidos" o "Tags Populares" en un futuro paso */}
            </div>

            {/* Filtro móvil + estadísticas + ordenación */}
            <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-4 mb-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Maquinaria disponible
                  </h2>
                  {/* <p className="text-sm text-zinc-400 mt-1">
                    {pagination.total} {pagination.total === 1 ? 'producto encontrado' : 'productos encontrados'}
                  </p> */}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {/* Botón de filtros (solo para móvil) */}
                  <div className="md:hidden">
                    <button
                      onClick={() => setIsFilterPanelOpen(true)}
                      className="px-4 py-2 bg-zinc-800 rounded-lg text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors flex items-center gap-2"
                    >
                      <Filter className="w-4 h-4" />
                      <span>Filtros</span>
                      {activeFiltersCount > 0 && (
                        <span className="bg-[#FCAA0D] text-black text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem]">
                          {activeFiltersCount}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Ordenación */}
                  <div className="flex bg-zinc-800/50 rounded-lg p-1">
                    <button
                      onClick={() => setSortOrder("newest")}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        sortOrder === "newest"
                          ? "bg-[#FCAA0D] text-black font-medium"
                          : "text-zinc-400 hover:text-white"
                      }`}
                    >
                      Más recientes
                    </button>
                    <button
                      onClick={() => setSortOrder("price_asc")}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        sortOrder === "price_asc"
                          ? "bg-[#FCAA0D] text-black font-medium"
                          : "text-zinc-400 hover:text-white"
                      }`}
                    >
                      Precio: menor
                    </button>
                    <button
                      onClick={() => setSortOrder("price_desc")}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        sortOrder === "price_desc"
                          ? "bg-[#FCAA0D] text-black font-medium"
                          : "text-zinc-400 hover:text-white"
                      }`}
                    >
                      Precio: mayor
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Chips de filtros activos (solo en móvil) */}
            <div className="md:hidden mb-6">
              {activeFiltersCount > 0 && (
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-sm text-zinc-500">Filtros activos:</span>
                  {priceRange[0] > 0 || priceRange[1] < 50000 ? (
                    <FilterChip
                        label={`${new Intl.NumberFormat('es-ES', {style: 'currency', currency: 'EUR', maximumFractionDigits: 0}).format(priceRange[0])} - ${new Intl.NumberFormat('es-ES', {style: 'currency', currency: 'EUR', maximumFractionDigits: 0}).format(priceRange[1])}`}
                        selected={true}
                        onClick={() => setPriceRange([0, 50000])}
                    />
                  ) : null}

                  {/* Chips para categorías seleccionadas en móvil - CORREGIDO */}
                  {selectedParentCategory !== null && categoriesHierarchy?.find(c => c.id === selectedParentCategory) && (
                    <FilterChip
                      key={`mobile-cat-parent-${selectedParentCategory}`}
                      label={categoriesHierarchy.find(c => c.id === selectedParentCategory)?.name || 'Categoría'}
                      selected={true}
                      onClick={() => {
                        handleParentCategorySelect(null);
                      }}
                    />
                  )}
                  {selectedChildCategory !== null && selectedParentCategory !== null &&
                    categoriesHierarchy?.find(c => c.id === selectedParentCategory)?.children?.find(sub => sub.id === selectedChildCategory) && (
                    <FilterChip
                      key={`mobile-cat-child-${selectedChildCategory}`}
                      label={categoriesHierarchy.find(c => c.id === selectedParentCategory)?.children?.find(sub => sub.id === selectedChildCategory)?.name || 'Subcategoría'}
                      selected={true}
                      onClick={() => {
                        handleChildCategorySelect(null);
                      }}
                    />
                  )}

                  {selectedBrands.map(brand => (
                    <FilterChip
                      key={`brand-${brand}`}
                      label={brand}
                      selected={true}
                      onClick={() => toggleBrand(brand)}
                    />
                  ))}

                  {selectedLocations.map(location => (
                    <FilterChip
                      key={`location-${location}`}
                      label={location}
                      selected={true}
                      onClick={() => toggleLocation(location)}
                    />
                  ))}

                  <button
                    onClick={resetFilters}
                    className="text-sm text-[#FCAA0D] hover:underline flex items-center gap-1"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Limpiar filtros
                  </button>
                </div>
              )}
            </div>

            {/* Grid de productos */}
            <AnimatePresence mode="wait">
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                initial={{ opacity: 0.8 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                {/* Indicador de carga sólo durante actualizaciones de filtros, no en la carga inicial */}
                {!isFirstLoad && listingsLoading ? (
                  <div className="col-span-1 md:col-span-2 lg:col-span-3 min-h-[200px] flex items-center justify-center">
                    <LoadingSpinner size="small" />
                  </div>
                ) : products.length > 0 ? (
                  <AnimatePresence>
                    {products.map(renderProduct)}
                  </AnimatePresence>
                ) : (
                  <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-zinc-900 rounded-lg border border-zinc-800 p-12 flex flex-col items-center justify-center text-center">
                    <div className="bg-zinc-800/50 p-4 rounded-full mb-6">
                      <Search className="w-10 h-10 text-zinc-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">No se encontraron productos</h3>
                    <p className="text-zinc-400 mb-6 max-w-md">
                      No hay productos que coincidan con tus criterios de búsqueda. Prueba a cambiar los filtros o realizar una búsqueda diferente.
                    </p>
                    <button
                      onClick={resetFilters}
                      className="px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors flex items-center gap-2"
                    >
                      <Settings2 className="w-4 h-4" />
                      Restablecer filtros
                    </button>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Paginación */}
            {!listingsLoading && products.length > 0 && (
              <div className="mt-10 flex justify-center">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
                    disabled={currentPage === 1}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                      currentPage === 1
                        ? "bg-zinc-800/30 text-zinc-600 cursor-not-allowed"
                        : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white"
                    }`}
                  >
                    <ArrowRight className="w-4 h-4 transform rotate-180" />
                  </button>

                  {(() => {
                    const pageNumbers = [];
                    const totalPages = pagination.pages;
                    const currentPageNum = currentPage;
                    const pageNeighbours = 1; // Páginas a mostrar a cada lado de la actual

                    // Siempre mostrar la primera página
                    pageNumbers.push(1);

                    // Elipses izquierdas
                    if (currentPageNum > pageNeighbours + 2) {
                      pageNumbers.push("...");
                    }

                    // Páginas alrededor de la actual
                    const startPage = Math.max(2, currentPageNum - pageNeighbours);
                    const endPage = Math.min(totalPages - 1, currentPageNum + pageNeighbours);

                    for (let i = startPage; i <= endPage; i++) {
                      if (i !== 1 && i !== totalPages) { // Evitar duplicados
                        pageNumbers.push(i);
                      }
                    }

                    // Elipses derechas
                    if (currentPageNum < totalPages - pageNeighbours - 1) {
                       // Evitar elipses si la siguiente página es la última o penúltima
                       if (endPage < totalPages -1) {
                          pageNumbers.push("...");
                       }
                    }

                    // Siempre mostrar la última página (si es diferente de la primera)
                    if (totalPages > 1) {
                      pageNumbers.push(totalPages);
                    }

                    // Eliminar duplicados de las elipses si están justo al lado de 1 o totalPages
                    const uniquePageNumbers = [...new Set(pageNumbers.filter((item, index, self) => {
                      // Mantener elipses si no están adyacentes a números que ya están en su lugar
                      if (item === "...") {
                        const prev = self[index - 1];
                        const next = self[index + 1];
                        if (typeof prev === 'number' && typeof next === 'number' && next === prev + 1) {
                          return false;
                        }
                      }
                      return true;
                    }))];


                    return uniquePageNumbers.map((page, index) =>
                      typeof page === "number" ? (
                        <button
                          key={`page-${page}`}
                          onClick={() => paginate(page)}
                          className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === page
                              ? "bg-[#FCAA0D] text-black"
                              : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white"
                          }`}
                        >
                          {page}
                        </button>
                      ) : (
                        <span
                          key={`ellipsis-${index}`}
                          className="w-10 h-10 flex items-center justify-center text-zinc-500"
                        >
                          ...
                        </span>
                      )
                    );
                  })()}

                  <button
                    onClick={() => paginate(currentPage < pagination.pages ? currentPage + 1 : pagination.pages)}
                    disabled={currentPage === pagination.pages}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                      currentPage === pagination.pages
                        ? "bg-zinc-800/30 text-zinc-600 cursor-not-allowed"
                        : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white"
                    }`}
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer del marketplace */}
      <footer className="mt-16 py-12 border-t border-zinc-800/50 bg-zinc-900/30 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-[#FCAA0D] font-medium text-base tracking-wider uppercase mb-2 block">
              MARKETPLACE DE MAQUINARIA DE ALIMENTACIÓN
            </span>
            <h1 className="text-3xl font-bold text-white mb-6">
              Maquinaria de ocasión
            </h1>
            <p className="text-zinc-400 mb-6 max-w-2xl mx-auto">
              Explore nuestra selección de maquinaria industrial de segunda mano,
              completamente revisada y garantizada por expertos.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <h3 className="text-white font-semibold text-lg mb-4">Sobre este marketplace</h3>
              <p className="text-zinc-400 text-sm">
                Nuestro marketplace de maquinaria de ocasión ofrece equipos de alta calidad,
                revisados y garantizados por nuestro equipo de expertos en tecnología alimentaria.
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold text-lg mb-4">Información de contacto</h3>
              <div className="space-y-3">
                <a href="tel:+34900000000" className="text-zinc-400 text-sm flex items-center gap-2 hover:text-[#FCAA0D] transition-colors">
                  <Phone className="w-4 h-4" />
                  +34 900 000 000
                </a>
                <a href="mailto:info@innotecfood.com" className="text-zinc-400 text-sm flex items-center gap-2 hover:text-[#FCAA0D] transition-colors">
                  <Mail className="w-4 h-4" />
                  info@innotecfood.com
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-white font-semibold text-lg mb-4">Garantía Innotec</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#FCAA0D] mt-0.5" />
                  <span className="text-zinc-400 text-sm">Maquinaria revisada por técnicos especializados</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#FCAA0D] mt-0.5" />
                  <span className="text-zinc-400 text-sm">Posibilidad de visita presencial antes de la compra</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-zinc-800/50 flex flex-col md:flex-row justify-between items-center">
            <p className="text-zinc-500 text-sm mb-4 md:mb-0">
              © {new Date().getFullYear()} Innotec Food. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-4">
              <a href="/condiciones-uso" className="text-zinc-500 text-sm hover:text-[#FCAA0D] transition-colors">
                Condiciones de uso
              </a>
              <a href="/politica-privacidad" className="text-zinc-500 text-sm hover:text-[#FCAA0D] transition-colors">
                Política de privacidad
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
